import { breakpoints, classes } from "@theme/helpers";
import { HTMLShopifySectionElement } from "@theme/html-custom-element";

const DESKTOP_HOVER = `(min-width: ${breakpoints.medium}px) and (hover: hover)`;
const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";

class BlogFeed extends HTMLShopifySectionElement {
  #headingRaf = null;

  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    if (!this.refs.post) return;

    this.desktopHoverMql = window.matchMedia(DESKTOP_HOVER);
    this.reducedMotionMql = window.matchMedia(REDUCED_MOTION);

    this.defaultBlockId = this.getDefaultBlockId();

    if (this.defaultBlockId) {
      this.activate(this.defaultBlockId);
    }

    this.on("mouseenter", this.refs.post.all, (event) => {
      if (!this.desktopHoverMql.matches) return;

      this.classList.add("is-hovered");
      this.activate(event.currentTarget.dataset.block);
    });

    this.on("focusin", this.refs.post.all, (event) => {
      if (!this.desktopHoverMql.matches) return;

      this.classList.add("is-hovered");
      this.activate(event.currentTarget.dataset.block);
    });

    this.on("mouseleave", this, () => {
      if (!this.desktopHoverMql.matches) return;

      this.reset();
    });

    this.on("click", this.refs.post.all, (event) => {
      if (this.desktopHoverMql.matches) return;

      this.activate(event.currentTarget.dataset.block);
    });

    if (this.refs.heading) {
      this.onHeadingMediaChange = () => this.updateHeadingOpacity();
      this.reducedMotionMql.addEventListener("change", this.onHeadingMediaChange);

      this.on("scroll", window, () => this.scheduleHeadingUpdate(), {
        passive: true,
      });
      this.on("resize", window, () => this.scheduleHeadingUpdate(), {
        passive: true,
      });

      /**
       * Posts animate their position/size on hover; recompute the overlap once
       * those transitions finish so the heading dim stays accurate.
       */
      if (this.refs.stage) {
        this.on("transitionend", this.refs.stage, (event) => {
          if (["top", "left", "width"].includes(event.propertyName)) {
            this.scheduleHeadingUpdate();
          }
        });
      }

      this.updateHeadingOpacity();
    }
  }

  disconnectedCallback() {
    if (this.#headingRaf) {
      cancelAnimationFrame(this.#headingRaf);
    }

    if (this.onHeadingMediaChange) {
      this.reducedMotionMql?.removeEventListener(
        "change",
        this.onHeadingMediaChange,
      );
    }

    if (super.disconnectedCallback) super.disconnectedCallback();
  }

  scheduleHeadingUpdate() {
    if (this.#headingRaf) return;

    this.#headingRaf = requestAnimationFrame(() => {
      this.#headingRaf = null;
      this.updateHeadingOpacity();
    });
  }

  /**
   * Dims the heading whenever its title visually overlaps (sits underneath) any
   * of the posts, so it recedes as posts scroll over it.
   */
  updateHeadingOpacity() {
    if (!this.refs.heading || this.reducedMotionMql.matches) {
      this.classList.remove("is-heading-dimmed");
      return;
    }

    const title =
      this.refs.heading.querySelector(".blog-feed__title") ?? this.refs.heading;
    const headingRect = title.getBoundingClientRect();

    const overlapping = this.refs.post.all.some((post) =>
      this.#rectsOverlap(headingRect, post.getBoundingClientRect()),
    );

    this.classList.toggle("is-heading-dimmed", overlapping);
  }

  /**
   * @param {DOMRect} a
   * @param {DOMRect} b
   * @returns {boolean}
   */
  #rectsOverlap(a, b) {
    return (
      a.left < b.right &&
      a.right > b.left &&
      a.top < b.bottom &&
      a.bottom > b.top
    );
  }

  /**
   * @returns {string | undefined}
   */
  getDefaultBlockId() {
    const centerPost = this.refs.post.all.find((post) =>
      post.classList.contains("blog-feed__post--center"),
    );

    return centerPost?.dataset.block ?? this.refs.post.all[0]?.dataset.block;
  }

  /**
   * @param {string} blockId
   */
  activate(blockId) {
    for (const post of this.refs.post.all) {
      const isActive = post.dataset.block === blockId;

      post.classList.toggle(classes.active, isActive);
    }
  }

  reset() {
    this.classList.remove("is-hovered");

    if (this.defaultBlockId) {
      this.activate(this.defaultBlockId);
    }
  }

  /**
   * @param {object} event
   */
  onBlockSelect(event) {
    this.classList.add("is-hovered");
    this.activate(event.detail.blockId);
  }

  /**
   * @param {object} event
   */
  onBlockDeselect() {
    this.reset();
  }
}

if (!customElements.get("blog-feed"))
  customElements.define("blog-feed", BlogFeed);
